import React, { useEffect, useState } from "react";
import CardDiv from "modules/common/card-div/CardDiv";
import IncentiveSlabFilter from "./IncentiveSlabFilter";
import { Button, Modal } from "react-bootstrap";
import { Drawer, Table } from "antd";
import { useIncentiveSlab } from "./store";
import { get } from "lodash";

const IncentiveSlab = () => {
  const [visible, setVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalShow, setModalShow] = useState(false);
  const [triggerFetch, setTriggerFetch] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  //state to handle queryparams
  const [queryParam, setQueryParam] = useState({
    employeeType: undefined,
    incentiveType: undefined,
  });

  //state to handle edit form fields
  const [formData, setFormData] = useState({
    incentivePercentage: "",
    month: "",
  });

  const [initialFormData, setInitialFormData] = useState({
    incentivePercentage: "",
    month: "",
  });

  const {
    incentiveSlabData,
    fetchIncentiveSlab,
    fetchIncentiveSlabFilter,
    fetchIncentiveSlabEdit,
  } = useIncentiveSlab();

  //Changing parameters to query parameters
  const buildQueryString = (data) => {
    const params = new URLSearchParams();
    for (const key in data) {
      if (Array.isArray(data[key])) {
        params.append(
          key,
          `[${data[key].map((value) => `'${value}'`).join(",")}]`
        );
      } else if (data[key] !== undefined && data[key] !== "") {
        params.append(key, data[key]);
      }
    }
    return `?${params.toString()}`;
  };

  //func to convert params as JSON.stringify
  const filterAndTransformParams = (params) => {
    return Object.fromEntries(
      Object.entries(params).filter(([key, value]) =>
        Array.isArray(value) ? value.length > 0 : value
      )
    );
  };

  //load on initial mount
  useEffect(() => {
    fetchIncentiveSlabFilter();
  }, []);

  //load Table data
  useEffect(() => {
    if (triggerFetch) {
      const updatedFilters = filterAndTransformParams(queryParam);
      const queryString = buildQueryString(updatedFilters);
      fetchIncentiveSlab(queryString);
    }
    setTriggerFetch(false);
  }, [triggerFetch, queryParam]);

  useEffect(() => {
    if (selectedUser) {
      const initialData = {
        incentivePercentage: get(selectedUser, "incentivePercentage", ""),
        month: get(selectedUser, "month", ""),
      };
      setFormData(initialData);
      setInitialFormData(initialData);
    }
  }, [selectedUser]);

  const getIncentiveSlabId = (month) => {
    const found = incentiveSlabData.rows_data.find(
      (item) => item.month.toString() === month.toString()
    );
    return found ? found.incentiveSlabId : null;
  };

  const handleAction = (record) => {
    setModalShow(true);
    setSelectedUser(record);
  };

  //columns for table
  const columns = [
    ...incentiveSlabData.headers,
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Button onClick={() => handleAction(record)}>Edit</Button>
      ),
      fixed: "right",
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrorMessage("");

    if (name === "incentivePercentage") {
      if (value === "") {
        setErrorMessage("Incentive percentage cannot be empty.");
        setFormData((prev) => ({
          ...prev,
          [name]: "",
        }));
      } else {
        const integerValue = parseInt(value, 10);

        if (isNaN(integerValue)) {
          setErrorMessage("Incentive percentage must be an integer.");
        } else if (integerValue > 500) {
          setErrorMessage("Incentive percentage cannot be more than 500.");
          setFormData((prev) => ({
            ...prev,
            [name]: integerValue,
          }));
        } else if (integerValue < 0) {
          setErrorMessage("Incentive percentage cannot be negative.");
          setFormData((prev) => ({
            ...prev,
            [name]: integerValue,
          }));
        } else {
          setErrorMessage(""); 
          setFormData((prev) => ({
            ...prev,
            [name]: integerValue,
          }));
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCloseModal = () => {
    setModalShow(false);
    setErrorMessage("");
    setFormData(initialFormData);
  };
  // save button for Edit
  const handleSave = () => {
    const incentivePercentage = parseInt(formData.incentivePercentage, 10);

    if (isNaN(incentivePercentage)) {
      setErrorMessage("Incentive percentage must be an integer.");
      return;
    } else if (incentivePercentage > 500) {
      setErrorMessage("Incentive percentage cannot be more than 500.");
      return;
    } else if (incentivePercentage < 0) {
      setErrorMessage("Incentive percentage cannot be negative.");
      return;
    }

    const incentiveSlabId = getIncentiveSlabId(formData.month);
    const payload = {
      incentivePercentage: incentivePercentage,
      incentiveSlabId: incentiveSlabId,
    };

    const updatedFilters = filterAndTransformParams(queryParam);
    const queryString = buildQueryString(updatedFilters);
    fetchIncentiveSlabEdit(payload, queryString);
    setModalShow(false);
    setSelectedUser(null);
    setErrorMessage("");
  };

  //After Applying filter
  const handleApplyFilters = (filters) => {
    let updatedFilters = { ...filters };

    const mergedFilters = {
      ...queryParam,
      ...updatedFilters,
    };
    setQueryParam(mergedFilters);
    setTriggerFetch(true);
    setCurrentPage(1);
    setPageSize(10);
    setVisible(!visible);
  };

  //Pagination
  const handleTableChange = (pagination) => {
    const updatedPagination = {
      ...queryParam,
      pageSize: pagination.pageSize,
      pageNumber: pagination.current,
    };
    setPageSize(pagination.pageSize);
    setCurrentPage(pagination.current);
    setQueryParam(updatedPagination);
    const updatedFilters = filterAndTransformParams(updatedPagination);
    setQueryParam(updatedFilters);
    setTriggerFetch(true);
  };

  return (
    <>
      <CardDiv
        title={"Incentives Slab Dashboard"}
        component={
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 10,
              }}
            >
              <Button
                type="primary"
                onClick={() => setVisible(true)}
                shape="round"
                style={{ marginRight: 10 }}
              >
                Filter
              </Button>
              <Drawer
                title="Filter for Incentives Slab"
                placement="right"
                onClose={() => setVisible(false)}
                visible={visible}
                width="30%"
                zIndex={999999999}
              >
                <IncentiveSlabFilter
                  onApplyFilters={handleApplyFilters}
                  onClose={() => setVisible(!visible)}
                  setQueryParam={setQueryParam}
                  setCurrentPage={setCurrentPage}
                  setPageSize={setPageSize}
                  setTriggerFetch={setTriggerFetch}
                />
              </Drawer>
            </div>
            <Modal
              show={modalShow}
              onHide={handleCloseModal}
              size="lg"
              aria-labelledby="contained-modal-title-vcenter"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                  Edit Details
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedUser !== null && (
                  <form>
                    <div className="mb-3">
                      <label htmlFor="month" className="form-label">
                        Month
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="month"
                        name="month"
                        value={formData.month}
                        readOnly
                      />
                    </div>
                    <div className="mb-3">
                      <label
                        htmlFor="incentivePercentage"
                        className="form-label"
                      >
                        Incentive Percentage
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="incentivePercentage"
                        name="incentivePercentage"
                        value={formData.incentivePercentage}
                        onChange={handleInputChange}
                        onKeyPress={(e) => {
                          if (
                            !/[0-9]/.test(e.key) &&
                            ![
                              "Backspace",
                              "Delete",
                              "ArrowLeft",
                              "ArrowRight",
                            ].includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                    {errorMessage && (
                      <div className="alert alert-danger" role="alert">
                        {errorMessage}
                      </div>
                    )}
                  </form>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={handleSave}>Save</Button>
                <Button onClick={handleCloseModal}>Close</Button>
              </Modal.Footer>
            </Modal>
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={incentiveSlabData.rows_data}
                onChange={handleTableChange}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: incentiveSlabData.total_row_count,
                  showSizeChanger: true,
                }}
                scroll={{ x: "max-content" }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default IncentiveSlab;
